import { SubmissionOutcome } from './submission-outcome';
import { PublicationVisibility, type BookIntent } from './publication-visibility';
import { ProviderMatcher } from './provider-matcher';
import { BOOK_LANGUAGE_NAMES_EN } from '@living-library/contracts';
import { LoadingNotice, ReviewSkeleton } from './loading';
import { ReportCard } from './report-card';
import { REPORT_COPY, reportLocale, type ReportLocale } from './report-copy';
export { ReportCard } from './report-card';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { button, card, input, CoverEditor, MetadataEditor, MetadataPreview } from './editors';
import {
  makeRequest,
  editableMetadata,
  sharedCorrectionDraft,
  decisionSender,
  coverReady,
  conflict,
  errorMessage,
  groupReports,
  identityChanged,
  validMetadata,
  type Book,
  type Cover,
  type Detail,
  type Metadata,
  type Report,
  type Request,
  type Submission,
} from './model';

export function CatalogueContributions({
  getAccessToken,
  onAccessDenied,
}: {
  getAccessToken(): Promise<string>;
  onAccessDenied(): void;
}) {
  const request = useMemo(
    () => makeRequest(getAccessToken, onAccessDenied),
    [getAccessToken, onAccessDenied],
  );
  const [tab, setTab] = useState<'submissions' | 'reports' | 'books'>('submissions');
  const [status, setStatus] = useState('pending');
  const [items, setItems] = useState<(Submission | Report)[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [bookId, setBookId] = useState('');
  const [bookIntent, setBookIntent] = useState<BookIntent>('edit');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [notice, setNotice] = useState('');
  const [reviewLocale, setReviewLocale] = useState<ReportLocale>(reportLocale);
  useEffect(() => {
    if (tab === 'books') return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setItems([]);
    request<{ items: (Submission | Report)[] }>(`/${tab}?status=${encodeURIComponent(status)}`)
      .then((result) => {
        if (!cancelled) setItems(result.items);
      })
      .catch((e) => {
        if (!cancelled) setError(errorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [request, tab, status, refresh]);
  const done = (message?: string) => {
    setNotice(message ?? '');
    setSelected(null);
    setRefresh((v) => v + 1);
  };
  return (
    <div className="min-w-0 space-y-6 text-ink">
      <header className="admin-page-header">
        <p className="admin-eyebrow">Catalogue moderation</p>
        <h1>Catalogue contributions</h1>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-ink-soft">
          Review reader publication requests, reports and shared edition corrections.
        </p>
      </header>
      <nav aria-label="Catalogue moderation queues" className="admin-tabs">
        {(['submissions', 'reports', 'books'] as const).map((v) => (
          <button
            type="button"
            key={v}
            aria-pressed={tab === v}
            onClick={() => {
              // A different queue renders a different DTO shape in this same commit.
              setItems([]);
              setTab(v);
              setStatus('pending');
              setSelected(null);
              setBookIntent('edit');
            }}
          >
            {v === 'submissions'
              ? 'Publication requests'
              : v === 'reports'
                ? 'Reports'
                : 'Shared book editor'}
          </button>
        ))}
      </nav>
      {notice && (
        <p role="status" className="rounded-xl bg-thread-soft p-4 text-sm">
          {notice}
        </p>
      )}
      {tab !== 'books' && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="min-w-44 text-sm font-semibold">
            Status
            <select
              className={input}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setSelected(null);
              }}
            >
              {(tab === 'submissions'
                ? ['pending', 'changes_requested', 'declined', 'withdrawn', 'published', 'matched']
                : ['pending', 'resolved', 'dismissed']
              ).map((v) => (
                <option key={v} value={v}>
                  {v.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </label>
          <button
            className={button}
            disabled={loading}
            onClick={() => {
              setSelected(null);
              setRefresh((v) => v + 1);
            }}
          >
            Refresh queue
          </button>
          {tab === 'reports' && (
            <label className="text-sm font-semibold">
              {REPORT_COPY[reviewLocale].language}
              <select
                className={input}
                value={reviewLocale}
                onChange={(event) => setReviewLocale(event.target.value as ReportLocale)}
              >
                <option value="en">English</option>
                <option value="sk">Slovenčina</option>
                <option value="cs">Čeština</option>
              </select>
            </label>
          )}
        </div>
      )}
      {tab === 'books' ? (
        <>
          <form
            className={`${card} flex flex-wrap items-end gap-3`}
            onSubmit={(e) => {
              e.preventDefault();
              setBookIntent('edit');
              setSelected(bookId.trim());
            }}
          >
            <label className="min-w-0 grow text-sm font-semibold">
              Shared book ID
              <input
                required
                className={input}
                value={bookId}
                onChange={(e) => setBookId(e.target.value)}
              />
            </label>
            <button className="admin-button-primary">Open book</button>
          </form>
          {selected && (
            <BookLoader
              key={`${selected}:${bookIntent}`}
              id={selected}
              request={request}
              intent={bookIntent}
            />
          )}
        </>
      ) : (
        <>
          {error && (
            <p role="alert" className="text-warning">
              {error}
            </p>
          )}
          {loading && (
            <div className={card}>
              <LoadingNotice
                label="Loading queue…"
                detail="Checking publication requests and their latest status."
              />
              <div aria-hidden="true" className="mt-5 grid gap-4 sm:grid-cols-3">
                {[0, 1, 2].map((item) => (
                  <div key={item} className="catalogue-skeleton h-24 rounded-xl" />
                ))}
              </div>
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <p className="admin-empty">No records with this status.</p>
          )}
          {tab === 'submissions' ? (
            <div className="grid min-w-0 items-start gap-5 xl:grid-cols-[minmax(200px,240px)_minmax(0,1fr)]">
              <div className="min-w-0 space-y-3">
                {(items as Submission[]).map((item) => (
                  <button
                    key={item.id}
                    className={`${card} block w-full cursor-pointer text-left transition-colors hover:bg-panel-raised focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-thread ${selected === item.id ? '!border-thread !bg-thread-soft' : ''}`}
                    aria-pressed={selected === item.id}
                    onClick={() => setSelected(item.id)}
                  >
                    <strong className="block break-words font-serif text-xl leading-snug">
                      {item.metadata.title}
                    </strong>
                    <span className="mt-2 block text-sm text-ink-muted">
                      {item.metadata.authors.join(', ')}
                    </span>
                    <span className="mt-4 block text-xs capitalize text-ink-soft">
                      {item.status.replaceAll('_', ' ')} ·{' '}
                      {new Date(item.createdAt).toLocaleDateString()}
                    </span>
                  </button>
                ))}
              </div>
              {selected ? (
                <SubmissionLoader
                  key={selected}
                  id={selected}
                  request={request}
                  done={done}
                  openBook={(id, intent = 'edit') => {
                    setTab('books');
                    setBookId(id);
                    setBookIntent(intent);
                    setSelected(id);
                  }}
                />
              ) : !loading && items.length > 0 ? (
                <div className="admin-empty py-16">
                  <h2 className="font-serif text-2xl text-ink">Choose a publication request</h2>
                  <p className="mt-2 text-sm">
                    Find a catalogue match or review the edition for publication.
                  </p>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="space-y-5">
              <p className="max-w-4xl text-sm leading-6 text-ink-muted" lang={reviewLocale}>
                {REPORT_COPY[reviewLocale].policy}
              </p>
              {groupReports(items as Report[]).map(([id, reports]) => (
                <section className={card} key={id}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="font-serif text-xl">
                      {reports[0]?.bookTitle ||
                        reports[0]?.bookId ||
                        REPORT_COPY[reviewLocale].providerEdition}
                    </h2>
                    {reports[0]?.bookId && (
                      <button
                        className={button}
                        onClick={() => {
                          setTab('books');
                          setBookId(reports[0]!.bookId!);
                          setBookIntent('edit');
                          setSelected(reports[0]!.bookId!);
                        }}
                      >
                        Inspect and correct book
                      </button>
                    )}
                  </div>
                  {reports.map((report) => (
                    <ReportCard
                      key={`${report.id}:${report.version}`}
                      report={report}
                      request={request}
                      done={done}
                      locale={reviewLocale}
                    />
                  ))}
                </section>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
function SubmissionLoader({
  id,
  request,
  done,
  openBook,
}: {
  id: string;
  request: Request;
  done(): void;
  openBook(id: string, intent?: BookIntent): void;
}) {
  const [detail, setDetail] = useState<Detail | null>(null),
    [error, setError] = useState(''),
    [refresh, setRefresh] = useState(0);
  const reload = () => {
    setDetail(null);
    setRefresh((v) => v + 1);
  };
  useEffect(() => {
    let alive = true;
    setError('');
    request<Detail>(`/submissions/${encodeURIComponent(id)}`)
      .then((v) => {
        if (alive) setDetail(v);
      })
      .catch((e) => {
        if (alive) setError(errorMessage(e));
      });
    return () => {
      alive = false;
    };
  }, [id, request, refresh]);
  return detail ? (
    <SubmissionEditor
      key={refresh}
      detail={detail}
      request={request}
      done={done}
      reload={reload}
      openBook={openBook}
    />
  ) : error ? (
    <div className={card}>
      <p role="alert" className="text-sm text-warning">
        {error}
      </p>
      <button className={`${button} mt-4`} onClick={reload}>
        Reload request
      </button>
    </div>
  ) : (
    <ReviewSkeleton />
  );
}
function useDecision(request: Request) {
  const send = useMemo(() => decisionSender(request), [request]);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [stale, setStale] = useState(false);
  const act = async (path: string, body: Record<string, unknown>, done: () => void) => {
    if (busy || stale) return;
    setBusy(true);
    setError('');
    try {
      await send(path, body);
      done();
    } catch (e) {
      setError(errorMessage(e));
      setStale(conflict(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, stale, act };
}
function useCover(request: Request, book: Book | null, draft?: Cover | null) {
  const initial: Cover = draft ?? {
    policy: book?.coverPolicy ?? 'automatic',
    ...(book?.coverPolicy === 'moderator' && book.coverImageUrl ? { url: book.coverImageUrl } : {}),
    ...(book?.coverRightsNote ? { rightsNote: book.coverRightsNote } : {}),
  };
  const [cover, setCover] = useState<Cover>(initial);
  const [uploading, setUploading] = useState(false);
  const [loadedUrl, loaded] = useState<string | null>(null),
    [failed, setFailed] = useState(false);
  return {
    cover,
    changed: JSON.stringify(cover) !== JSON.stringify(initial),
    uploading,
    ready: !uploading && coverReady(cover, loadedUrl),
    editor: (
      <CoverEditor
        request={request}
        uploading={uploading}
        setUploading={setUploading}
        value={cover}
        change={setCover}
        loadedUrl={loadedUrl}
        loaded={loaded}
        failed={failed}
        setFailed={setFailed}
      />
    ),
  };
}
export function SubmissionEditor({
  detail,
  request,
  done,
  reload,
  openBook,
}: {
  detail: Detail;
  request: Request;
  done(): void;
  reload(): void;
  openBook?(id: string, intent?: BookIntent): void;
}) {
  const { submission, currentBook } = detail;
  // Also protect clients talking to an older API during rollout.
  const candidates = detail.candidates.filter(
    (book) => ![submission.bookId, currentBook?.id, submission.publishedBookId].includes(book.id),
  );
  const [metadata, setMetadata] = useState(submission.metadata),
    [message, setMessage] = useState(''),
    [target, setTarget] = useState<Book | null>(null),
    [matchingBusy, setMatchingBusy] = useState(false);
  const state = useDecision(request),
    cover = useCover(request, currentBook, detail.moderatorCover);
  const changed = identityChanged(submission.metadata, metadata),
    pending = submission.status === 'pending';
  const selectedMatch = useRef<HTMLElement>(null);
  useEffect(() => {
    if (target) selectedMatch.current?.focus();
  }, [target]);
  const decide = (action: 'approve' | 'request_changes' | 'decline' | 'match') =>
    void state.act(
      `/submissions/${submission.id}/decision`,
      {
        expectedVersion: submission.version,
        action,
        moderatorMessage: message.trim() || undefined,
        ...(['approve', 'request_changes'].includes(action) ? { metadata } : {}),
        ...(['approve', 'request_changes'].includes(action) && cover.changed
          ? { cover: cover.cover }
          : {}),
        ...(action === 'match' && target
          ? { targetBookId: target.id, targetMetadataVersion: target.metadataVersion }
          : {}),
      },
      done,
    );
  const [mode, setMode] = useState<'match' | 'edit'>('match');
  const locked = state.busy || state.stale || !pending || matchingBusy || cover.uploading;
  const language =
    BOOK_LANGUAGE_NAMES_EN[submission.metadata.language] ?? submission.metadata.language;
  if (!pending) return <SubmissionOutcome detail={detail} reload={reload} openBook={openBook} />;
  return (
    <section className={`${card} catalogue-review`} aria-label="Review publication request">
      <header className="border-b border-line/65 pb-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="admin-eyebrow">Publication request</p>
          <span className="rounded-full bg-thread-soft px-3 py-1 text-xs font-semibold capitalize text-thread">
            {submission.status.replaceAll('_', ' ')}
          </span>
        </div>
        <h2 className="mt-3 break-words font-serif text-3xl leading-tight">
          {submission.metadata.title}
        </h2>
        <p className="mt-2 text-sm text-ink-soft">
          {submission.metadata.authorUnknown
            ? 'Unknown author'
            : submission.metadata.authors.join(', ')}
        </p>
        <p className="mt-3 text-xs leading-6 text-ink-muted">
          {[
            language,
            submission.metadata.publisher,
            submission.metadata.publishedYear,
            submission.metadata.binding,
            submission.metadata.isbn ? `ISBN ${submission.metadata.isbn}` : 'No ISBN supplied',
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {submission.moderatorMessage && (
          <p className="mt-4 text-sm">{submission.moderatorMessage}</p>
        )}
      </header>
      <nav aria-label="Publication review views" className="catalogue-review-views mt-5">
        <button
          type="button"
          aria-pressed={mode === 'match'}
          disabled={state.busy || matchingBusy || cover.uploading}
          onClick={() => setMode('match')}
        >
          Find a match
        </button>
        <button
          type="button"
          aria-pressed={mode === 'edit'}
          disabled={state.busy || matchingBusy || cover.uploading}
          onClick={() => setMode('edit')}
        >
          Edit &amp; publish
        </button>
      </nav>
      {changed && (
        <p
          role="status"
          className="mt-5 rounded-xl border border-warning/30 bg-warning/5 p-4 text-sm leading-6 text-warning"
        >
          Your corrected metadata will be used when publishing. Publishing upgrades the reader’s
          existing book and keeps their progress, notes and quotes. To search for an existing
          edition using changed identity details, request a reader revision first.
        </p>
      )}
      <fieldset className="min-w-0" disabled={locked}>
        <div hidden={mode !== 'match'}>
          <ProviderMatcher
            submission={submission}
            request={request}
            disabled={state.busy || state.stale || !pending || changed}
            select={setTarget}
            onBusy={setMatchingBusy}
          />
          {candidates.length > 0 && (
            <details className="mt-6 rounded-xl border border-line/65 p-4" open>
              <summary className="text-sm font-semibold">
                Already in Belore · {candidates.length} possible matches
              </summary>
              {candidates.map((book) => (
                <label
                  key={book.id}
                  className={`mt-3 block cursor-pointer rounded-xl border p-4 ${target?.id === book.id ? 'border-thread bg-thread-soft' : 'border-line/65 bg-panel-raised'}`}
                >
                  <input
                    type="radio"
                    className="size-4 accent-thread"
                    name={`match-${submission.id}`}
                    checked={target?.id === book.id}
                    onChange={() => setTarget(book)}
                  />{' '}
                  <strong>{book.title}</strong>
                  <MetadataPreview value={book} />
                </label>
              ))}
            </details>
          )}
          {target && (
            <section
              ref={selectedMatch}
              tabIndex={-1}
              aria-label="Selected match"
              className="mt-6 rounded-xl border border-thread/50 bg-thread-soft/50 p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold text-thread">Ready to compare</p>
                  <h3 className="mt-1 font-serif text-2xl">Selected catalogue edition</h3>
                </div>
                <button type="button" className={button} onClick={() => setTarget(null)}>
                  Clear selection
                </button>
              </div>
              <p className="mt-2 break-all text-xs text-ink-muted">
                {target.source?.replaceAll('_', ' ')} · {target.sourceId ?? target.id} · Metadata
                version {target.metadataVersion}
              </p>
              <div className="mt-5 grid gap-4 xl:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    Submitted edition
                  </p>
                  <MetadataPreview value={{ ...submission.metadata, description: null }} />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    Catalogue edition
                  </p>
                  <MetadataPreview value={{ ...target, description: null }} />
                </div>
              </div>
              <p className="mt-4 text-sm leading-6 text-ink-soft">
                Check language, ISBN, publisher, year and binding. The reader must accept your
                proposal before their book is linked.
              </p>
            </section>
          )}
        </div>
        <div hidden={mode !== 'edit'} className="pt-6">
          <p className="mb-6 text-sm leading-6 text-ink-muted">
            Review the submitted details and cover before adding this edition to the public
            catalogue. You can correct or complete the metadata and publish directly.
          </p>
          <MetadataEditor value={metadata} change={setMetadata} />
          {cover.editor}
          <details className="mt-6 rounded-xl border border-line/65 p-4">
            <summary className="text-sm font-semibold">Preview edited edition</summary>
            <MetadataPreview value={metadata} />
          </details>
        </div>
        <section
          hidden={mode === 'match' && !target}
          className="mt-7 border-t border-line/65 pt-6"
          aria-label="Moderation decision"
        >
          <h3 className="font-serif text-2xl">
            {mode === 'match' ? 'Propose this match' : 'Review decision'}
          </h3>
          <p className="mt-2 text-sm leading-6 text-ink-muted">
            {mode === 'match'
              ? 'Explain why this is the same edition. The reader will receive your message with the proposal.'
              : 'A message is required when requesting changes or declining.'}
          </p>
          <label className="mt-4 block text-sm font-semibold">
            Message to reader / decision reason
            <textarea
              className={input}
              rows={3}
              maxLength={1000}
              value={message}
              placeholder={
                mode === 'match'
                  ? 'Explain the edition match…'
                  : 'Tell the reader what needs attention…'
              }
              onChange={(e) => setMessage(e.target.value)}
            />
          </label>
          <div className="mt-4 flex flex-wrap gap-3">
            <div hidden={mode !== 'match'}>
              <button
                className="admin-button-primary"
                disabled={!target || !message.trim() || changed || matchingBusy}
                onClick={() => decide('match')}
              >
                Propose match
              </button>
            </div>
            <div hidden={mode !== 'edit'}>
              <div className="flex flex-wrap gap-3">
                <button
                  className="admin-button-primary"
                  disabled={!validMetadata(metadata) || !cover.ready}
                  onClick={() => decide('approve')}
                >
                  Publish edition
                </button>
                <button
                  className={button}
                  disabled={!message.trim() || !validMetadata(metadata) || !cover.ready}
                  onClick={() => decide('request_changes')}
                >
                  Request changes
                </button>
                <button
                  className="admin-button-danger"
                  disabled={!message.trim()}
                  onClick={() => decide('decline')}
                >
                  Decline
                </button>
              </div>
            </div>
          </div>
        </section>
      </fieldset>
      {state.busy && (
        <div className="mt-5">
          <LoadingNotice
            label="Saving your decision…"
            detail="Please keep this request open until it finishes."
          />
        </div>
      )}
      {state.error && (
        <p
          role="alert"
          className="mt-4 rounded-xl border border-warning/30 p-4 text-sm text-warning"
        >
          {state.error}
        </p>
      )}
      <details className="mt-7 border-t border-line/65 pt-5">
        <summary className="text-sm font-semibold text-ink-muted">
          Submitted snapshot and current book
        </summary>
        <div className="grid gap-4 xl:grid-cols-2">
          <div>
            <p className="mt-4 text-xs text-ink-muted">Submitted snapshot</p>
            <MetadataPreview value={submission.metadata} />
          </div>
          <div>
            <p className="mt-4 text-xs text-ink-muted">Current private book</p>
            {currentBook ? (
              <MetadataPreview value={currentBook} />
            ) : (
              <p>The original book is unavailable.</p>
            )}
          </div>
        </div>
      </details>
      <footer className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-line/65 pt-5">
        <p className="text-xs text-ink-muted">
          Version {submission.version} · Submitted{' '}
          {new Date(submission.createdAt).toLocaleDateString()}
        </p>
        <button
          className={button}
          disabled={state.busy || matchingBusy || cover.uploading}
          onClick={reload}
        >
          Reload request
        </button>
      </footer>
    </section>
  );
}

function BookLoader({ id, request, intent }: { id: string; request: Request; intent: BookIntent }) {
  const [book, setBook] = useState<Book | null>(null),
    [error, setError] = useState(''),
    [refresh, setRefresh] = useState(0),
    [notice, setNotice] = useState(''),
    [openVisibility, setOpenVisibility] = useState(intent === 'visibility');
  const reload = useCallback(() => {
    setBook(null);
    setOpenVisibility(false);
    setRefresh((v) => v + 1);
  }, []);
  useEffect(() => {
    let alive = true;
    setError('');
    request<Book>(`/books/${encodeURIComponent(id)}`)
      .then((v) => {
        if (alive) setBook(v);
      })
      .catch((e) => {
        if (alive) setError(errorMessage(e));
      });
    return () => {
      alive = false;
    };
  }, [id, request, refresh]);
  return book ? (
    <>
      {notice && (
        <p role="status" className="rounded-xl bg-thread-soft p-4 text-sm">
          {notice}
        </p>
      )}
      <BookEditor
        key={refresh}
        book={book}
        request={request}
        reload={reload}
        initiallyOpenVisibility={openVisibility}
        saved={(action) => {
          setNotice(
            action === 'hide'
              ? 'Publication removed from the public catalogue.'
              : action === 'restore'
                ? 'Publication restored to the public catalogue.'
                : 'Publication changes saved.',
          );
          reload();
        }}
      />
    </>
  ) : (
    <p className="admin-empty" role="status">
      {error || 'Loading shared book…'}
      {error && (
        <button className={button} onClick={reload}>
          Reload book
        </button>
      )}
    </p>
  );
}
export function BookEditor({
  book,
  request,
  reload,
  initiallyOpenVisibility = false,
  saved,
}: {
  book: Book;
  request: Request;
  reload(): void;
  initiallyOpenVisibility?: boolean;
  saved?(action: 'correct' | 'hide' | 'restore'): void;
}) {
  const [metadata, setMetadata] = useState<Metadata>(() => editableMetadata(book)),
    [message, setMessage] = useState('');
  const state = useDecision(request),
    cover = useCover(request, book);
  const correction = sharedCorrectionDraft(book, metadata, cover.changed ? cover.cover : undefined);
  const decide = (action: 'correct' | 'hide' | 'restore', reason = message.trim()) =>
    void state.act(
      `/books/${book.id}/decision`,
      {
        action,
        expectedVersion: book.metadataVersion,
        moderatorMessage: reason,
        ...(action === 'correct' ? correction.payload : {}),
      },
      () => (saved ? saved(action) : reload()),
    );
  return (
    <section className={card}>
      <h2 className="font-serif text-2xl">Edit publication</h2>
      <p className="mt-2 font-serif text-xl">{book.title}</p>
      <p className="mt-2 text-xs text-ink-muted">
        {book.catalogueVisibility} · Metadata version {book.metadataVersion} · Source:{' '}
        {book.source ?? 'reader'}
      </p>
      <p className="mb-6 mt-3 text-sm leading-6 text-ink-muted">
        Corrections affect the shared edition. Preserve its identity and reader relationships.
      </p>
      <fieldset className="min-w-0" disabled={state.busy || state.stale || cover.uploading}>
        <MetadataEditor value={metadata} change={setMetadata} />
        <MetadataPreview value={metadata} />
        {cover.editor}
        <label className="mt-4 block">
          Reason for correction
          <textarea
            className={input}
            required
            maxLength={1000}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </label>
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            className="admin-button-primary"
            disabled={!message.trim() || !correction.canSave || (cover.changed && !cover.ready)}
            onClick={() => decide('correct')}
          >
            Save publication changes
          </button>
        </div>
        <p className="mt-3 text-xs leading-5 text-ink-muted">
          A correction reason is required to save changes and is recorded in moderation history.
        </p>
      </fieldset>
      <PublicationVisibility
        book={book}
        disabled={state.busy || state.stale || cover.uploading}
        busy={state.busy}
        initiallyOpen={initiallyOpenVisibility}
        confirm={decide}
      />
      {state.error && (
        <p role="alert" className="text-warning">
          {state.error}
        </p>
      )}
      <button
        className={`${button} mt-5`}
        disabled={state.busy || cover.uploading}
        onClick={reload}
      >
        Reload book
      </button>
    </section>
  );
}
